import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Phone, MessageCircle, Globe,
  ShieldCheck, Star, Tag, Layers, Palette, Ruler, Award,
  ExternalLink, ImageIcon, FileText, Play, AlertCircle, Loader2,
  Upload, Trash2, Eye, Plus, ArrowRight, HelpCircle, Download, X,
  CheckSquare, Square, Copy
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { guestAPI } from '../utils/api';
import axios from '../utils/axios';
import { API_BASE_URL } from '../config/api.config';
import toast from 'react-hot-toast';

/* ─── Premium Styling System ───────────────────────────────────────────────── */
const S = {
  page: (dark = false) => ({
    minHeight: '100vh',
    background: dark
      ? 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 40%, #16213e 100%)'
      : '#f8fafc',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: dark ? '#e2e8f0' : '#1e293b',
    paddingBottom: '80px',
  }),

  glass: (dark = false) => ({
    background: dark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    borderRadius: '16px',
    boxShadow: dark
      ? '0 20px 40px rgba(0, 0, 0, 0.25)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
  }),

  header: (dark = false) => ({
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    background: dark ? 'rgba(0,0,0,0.3)' : '#ffffff',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  }),

  brandLogo: {
    fontSize: '1.25rem',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #0A5F38, #C5A57D)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  },

  badge: (color = '#0A5F38') => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '0.7rem',
    fontWeight: 600,
    background: `${color}15`,
    border: `1px solid ${color}33`,
    color: color,
    letterSpacing: '0.4px',
  }),

  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px 20px',
  },

  /* Categories Sidebar/Tabs */
  categoryTab: (active) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '0.88rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    background: active ? '#0A5F38' : 'rgba(10, 95, 56, 0.06)',
    color: active ? '#ffffff' : '#0A5F38',
    marginRight: '8px',
    marginBottom: '8px',
  }),

  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '24px',
  },

  mediaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },

  mediaCard: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#0f172a',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },

  /* Dropzone block */
  dropzone: {
    border: '2px dashed rgba(10, 95, 56, 0.35)',
    borderRadius: '12px',
    background: 'rgba(10, 95, 56, 0.02)',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#0A5F38',
  },

  /* CTA Buttons */
  ctaBtn: (variant = 'primary') => {
    const variants = {
      primary: {
        background: 'linear-gradient(135deg, #0A5F38, #084c2e)',
        color: '#fff',
        border: 'none',
        boxShadow: '0 8px 20px rgba(10, 95, 56, 0.25)',
      },
      whatsapp: {
        background: 'linear-gradient(135deg, #25d366, #128c7e)',
        color: '#fff',
        border: 'none',
        boxShadow: '0 8px 20px rgba(37,211,102,0.25)',
      },
      outline: {
        background: 'rgba(255,255,255,0.05)',
        color: '#94a3b8',
        border: '1px solid rgba(255,255,255,0.18)',
      },
    };
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      borderRadius: '10px',
      fontSize: '0.88rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.2s',
      ...variants[variant],
    };
  },

  /* Lightbox Styles */
  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(5, 5, 10, 0.95)',
    backdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
};

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url}`;
};

/* ─── Video Thumbnail ──────────────────────────────────────────────────────── */
const VideoThumb = ({ src }) => (
  <div className="relative w-full h-full flex items-center justify-center bg-slate-950">
    <video src={getMediaUrl(src)} className="w-full h-full object-cover opacity-60" muted />
    <div className="absolute p-2 bg-black/60 rounded-full flex">
      <Play size={18} color="#fff" fill="#fff" />
    </div>
  </div>
);

/* ─── Document Thumbnail ───────────────────────────────────────────────────── */
const DocThumb = ({ originalName, isZip }) => (
  <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 p-3 bg-slate-900 border border-white/5 text-center">
    <FileText size={26} color={isZip ? '#f093fb' : '#a5b4fc'} />
    <span className="text-[10px] text-slate-400 font-medium block truncate max-w-full">
      {originalName || (isZip ? 'Archive.zip' : 'Document')}
    </span>
  </div>
);

/* ─── Zoomable Image for Lightbox ───────────────────────────────────────────── */
const ZoomableImage = ({ src, alt }) => {
  const [zoomStyle, setZoomStyle] = useState({ transform: 'scale(1)', transformOrigin: 'center' });

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transform: 'scale(2.2)',
      transformOrigin: `${x}% ${y}%`,
      cursor: 'zoom-in'
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({ transform: 'scale(1)', transformOrigin: 'center' });
  };

  return (
    <div className="overflow-hidden rounded-lg max-w-full max-h-[80vh] flex items-center justify-center bg-black/40 shadow-2xl border border-white/10">
      <img
        src={src}
        alt={alt}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          ...zoomStyle,
          transition: 'transform 0.15s ease-out, transform-origin 0s',
          maxWidth: '100%',
          maxHeight: '80vh',
          objectFit: 'contain'
        }}
      />
    </div>
  );
};

/* ─── Media Card Renderer ──────────────────────────────────────────────────── */
const MediaCardItem = ({ item, onSelect, onDelete, isAuth, isSelected, onToggleSelect }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={S.mediaCard}
      className={`group transform transition-all ${isSelected ? 'ring-2 ring-emerald-500 scale-[1.02]' : 'hover:scale-[1.03]'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        if (isSelected && onToggleSelect) {
          e.stopPropagation();
          onToggleSelect(item._id);
        } else {
          onSelect(e);
        }
      }}
    >
      {item.fileType === 'video' ? (
        <VideoThumb src={item.url} />
      ) : item.fileType === 'pdf' || item.fileType === 'document' || item.fileType === 'zip' ? (
        <DocThumb originalName={item.originalName} isZip={item.fileType === 'zip'} />
      ) : (
        <img
          src={getMediaUrl(item.url)}
          alt={item.originalName || 'Product catalog'}
          loading="lazy"
          className="w-full h-full object-contain transition-opacity duration-200 group-hover:opacity-90"
        />
      )}

      {isAuth && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (onToggleSelect) onToggleSelect(item._id);
          }}
          className={`absolute top-2 left-2 p-1.5 rounded-lg transition-all z-20 cursor-pointer ${isSelected ? 'opacity-100 bg-emerald-500 text-white shadow-md' : 'opacity-0 group-hover:opacity-100 bg-white/90 hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 shadow-sm border border-slate-200/50'}`}
        >
          {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
        </div>
      )}

      {isAuth && hovered && !isSelected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Delete this file?')) {
              onDelete(item._id);
            }
          }}
          className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg z-10 border border-red-500/20"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
};

/* ─── Product Gallery Main Component ───────────────────────────────────────── */
const toSlug = (name = '') =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const ProductGallery = () => {
  const { productToken, categoryId, categorySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isGuestMode = location.pathname.startsWith('/view/');
  const isAuth = !isGuestMode;

  /* States */
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [company, setCompany] = useState(null);
  const [viewMode, setViewMode] = useState('detail'); // 'detail' or 'list'
  const [categoryName, setCategoryName] = useState('Products');
  const [allProducts, setAllProducts] = useState([]);
  const [isGalleryView, setIsGalleryView] = useState(false);

  /* Lead submission states */
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadAddress, setLeadAddress] = useState('');
  const [leadPreferredDate, setLeadPreferredDate] = useState('');
  const [leadPreferredTime, setLeadPreferredTime] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState('');

  // Clear lead form state when activeProduct changes
  useEffect(() => {
    setLeadName('');
    setLeadPhone('');
    setLeadEmail('');
    setLeadAddress('');
    setLeadPreferredDate('');
    setLeadPreferredTime('');
    setLeadSuccess(false);
    setLeadError('');
  }, [activeProduct]);

  const isOutOfHours = (timeStr) => {
    if (!timeStr) return false;
    const [h, m] = timeStr.split(':').map(Number);
    const totalMinutes = h * 60 + m;
    return totalMinutes < 570 || totalMinutes > 1170; // 9:30 AM is 570, 7:30 PM is 1170
  };

  const getMinLeadDate = () => {
    const now = new Date();
    if (now.getHours() >= 12) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    return now.toISOString().split('T')[0];
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadPhone.trim()) {
      setLeadError('Phone number is required');
      return;
    }
    const cleanPhone = leadPhone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setLeadError('Please enter a valid 10-digit phone number');
      return;
    }
    if (!leadPreferredDate) {
      setLeadError('Preferred Date is required');
      return;
    }
    const minDateStr = getMinLeadDate();
    if (leadPreferredDate < minDateStr) {
      const todayStr = new Date().toISOString().split('T')[0];
      if (leadPreferredDate === todayStr && new Date().getHours() >= 12) {
        setLeadError('Same-day callback requests are closed after 12:00 PM. Please select tomorrow or a future date.');
      } else {
        setLeadError('Preferred Date cannot be in the past. Please select a valid future date.');
      }
      return;
    }
    if (!leadPreferredTime) {
      setLeadError('Preferred Time is required');
      return;
    }
    const todayStr = new Date().toISOString().split('T')[0];
    if (leadPreferredDate === todayStr) {
      const now = new Date();
      const [selH, selM] = leadPreferredTime.split(':').map(Number);
      if (selH < now.getHours() || (selH === now.getHours() && selM <= now.getMinutes())) {
        setLeadError('Preferred Time cannot be in the past. Please select a future time slot for today.');
        return;
      }
    }

    try {
      setLeadSubmitting(true);
      setLeadError('');

      const payload = {
        name: leadName || 'Guest',
        phone: cleanPhone,
        email: leadEmail,
        address: leadAddress,
        preferredDate: leadPreferredDate,
        preferredTime: leadPreferredTime,
        productToken: productToken,
        requirements: [activeProduct?.name || 'General Inquiry']
      };

      const res = await guestAPI.submitLead(payload);
      if (res.data?.success) {
        setLeadSuccess(true);
        toast.success('Your request has been submitted successfully!');
      } else {
        setLeadError(res.data?.error || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Lead submission error:', err);
      setLeadError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setLeadSubmitting(false);
    }
  };

  /* Dynamic Guest Computations */
  const guestRelatedProducts = useMemo(() => {
    if (!activeProduct || !allProducts.length) return [];
    return allProducts.filter(p => p.categoryId === activeProduct.categoryId);
  }, [allProducts, activeProduct]);

  const guestGridProducts = useMemo(() => {
    if (!allProducts.length) return [];
    if (!activeCategory) return allProducts;
    return allProducts.filter(p => p.categoryId === activeCategory.id);
  }, [allProducts, activeCategory]);

  /* Lightbox states */
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  /* Upload state */
  const [uploadingProdId, setUploadingProdId] = useState(null);
  const [uploadModalProduct, setUploadModalProduct] = useState(null);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  /* 1. Fetching Data */
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get('/admin/products'),
        axios.get('/admin/categories')
      ]);

      const hiddenCategories = ['Other Category', 'Installation'];
      const sortedCategories = (categoriesRes.data || [])
        .filter(c => !hiddenCategories.includes(c.name?.trim()))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedCategories);
      setProducts(productsRes.data || []);

      if (sortedCategories.length > 0) {
        setActiveCategory(prev => {
          if (prev) {
            const stillExists = sortedCategories.find(c => c.id === prev.id);
            if (stillExists) return stillExists;
          }
          return sortedCategories[0];
        });
      }
    } catch (err) {
      console.error('Failed to load gallery details:', err);
      toast.error('Failed to load catalog resources');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGuestProduct = useCallback(async () => {
    try {
      setLoading(true);
      if (categoryId) {
        const res = await guestAPI.getCategory(categoryId);
        if (res.data?.success) {
          const cat = res.data.category;
          setCompany(res.data.companySettings);
          setAllProducts(res.data.allProducts || []);
          setCategories([cat]);
          setActiveCategory(cat);
          setCategoryName(cat.name);
          
          if (cat.name?.toLowerCase() === 'interiors') {
            const catProducts = res.data.allProducts || [];
            if (catProducts.length === 1) {
              setActiveProduct(catProducts[0]);
              setViewMode('detail');
              setIsGalleryView(false);
            } else {
              setViewMode('list');
              setIsGalleryView(true);
            }
          } else {
            setViewMode('list');
            setIsGalleryView(true);
          }
        } else {
          toast.error('Category link has expired or is invalid.');
        }
      } else {
        const res = await guestAPI.getProduct(productToken);
        if (res.data?.success) {
          const prod = res.data.product;
          setActiveProduct(prod);
          setCompany(res.data.companySettings);
          setAllProducts(res.data.allProducts || []);

          let cats = res.data.categories || [];
          const hiddenCategories = ['Other Category', 'Installation'];
          cats = cats.filter(c => !hiddenCategories.includes(c.name?.trim()));
          setCategories(cats);

          if (prod.categoryId) {
            const currentCat = cats.find(c => c.id === prod.categoryId);
            if (currentCat) {
              setActiveCategory(currentCat);
            }
          }

          setCategoryName(res.data.categoryName || 'Products');

          const searchParams = new URLSearchParams(location.search);
          if (searchParams.get('view') === 'all') {
            setActiveCategory(null);
            setViewMode('list');
            setIsGalleryView(true);
          }
        } else {
          toast.error('Gallery link has expired or is invalid.');
        }
      }
    } catch (err) {
      console.error('Guest load error:', err);
    } finally {
      setLoading(false);
    }
  }, [productToken, categoryId, location.search]);

  useEffect(() => {
    if (isAuth) {
      fetchProducts();
    } else {
      loadGuestProduct();
    }
  }, [isAuth, fetchProducts, loadGuestProduct]);

  /* 2. File Uploading & Deletion */
  const handleUploadFiles = async (productId, filesArray) => {
    if (!filesArray || filesArray.length === 0) return;

    try {
      setUploadingProdId(productId);
      const productObj = products.find(p => p.id === productId || p._id === productId);
      if (!productObj) return;

      const formData = new FormData();
      const existingCatalogs = (productObj.product?.catalogs || []).filter(c => c.url);

      const payloadProduct = {
        ...productObj.product,
        catalogs: existingCatalogs
      };

      formData.append('product', JSON.stringify(payloadProduct));
      formData.append('supplier', JSON.stringify(productObj.supplier || {}));

      for (let i = 0; i < filesArray.length; i++) {
        formData.append('catalogs', filesArray[i]);
      }

      await axios.put(`/admin/products/${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0 // Prevent timeout for large file uploads
      });

      toast.success('Gallery media updated successfully!');
      setUploadModalProduct(null);
      setSelectedUploadFiles([]);
      fetchProducts();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload files.');
    } finally {
      setUploadingProdId(null);
    }
  };

  const handleToggleMediaSelect = (fileId) => {
    setSelectedMediaIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleMultiDeleteFiles = async (productId) => {
    try {
      const productObj = products.find(p => p.id === productId || p._id === productId);
      if (!productObj) return;

      const idsToDelete = selectedMediaIds.filter(id =>
        (productObj.product?.catalogs || []).some(c => c._id === id)
      );

      if (idsToDelete.length === 0) return;
      if (!window.confirm(`Are you sure you want to delete ${idsToDelete.length} selected files?`)) return;

      setUploadingProdId(productId);

      const formData = new FormData();
      const updatedCatalogs = (productObj.product?.catalogs || []).filter(c => !idsToDelete.includes(c._id));

      const payloadProduct = {
        ...productObj.product,
        catalogs: updatedCatalogs
      };

      formData.append('product', JSON.stringify(payloadProduct));
      formData.append('supplier', JSON.stringify(productObj.supplier || {}));

      await axios.put(`/admin/products/${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`${idsToDelete.length} files deleted successfully.`);
      setSelectedMediaIds(prev => prev.filter(id => !idsToDelete.includes(id)));
      fetchProducts();
    } catch (err) {
      console.error('Multi-deletion error:', err);
      toast.error('Failed to delete selected files.');
    } finally {
      setUploadingProdId(null);
    }
  };

  const handleDeleteFile = async (productId, catalogId) => {
    try {
      const productObj = products.find(p => p.id === productId || p._id === productId);
      if (!productObj) return;

      const formData = new FormData();
      const updatedCatalogs = (productObj.product?.catalogs || []).filter(c => c._id !== catalogId);

      const payloadProduct = {
        ...productObj.product,
        catalogs: updatedCatalogs
      };

      formData.append('product', JSON.stringify(payloadProduct));
      formData.append('supplier', JSON.stringify(productObj.supplier || {}));

      await axios.put(`/admin/products/${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File deleted successfully.');
      fetchProducts();
    } catch (err) {
      console.error('Deletion error:', err);
      toast.error('Failed to delete file.');
    }
  };

  const handleCopyGalleryUrl = useCallback(() => {
    // Find the first product with a viewToken
    const firstProduct = products.find(p => p.viewToken);

    if (firstProduct && firstProduct.viewToken) {
      const url = `${window.location.origin}/view/product/${firstProduct.viewToken}?view=all`;
      navigator.clipboard.writeText(url)
        .then(() => toast.success('Guest gallery link copied to clipboard!'))
        .catch(() => toast.error('Failed to copy link.'));
    } else {
      toast.error('No products available to generate a gallery link. Please add a product first.');
    }
  }, [products]);

  /* Group products of the active category */
  const activeProducts = useMemo(() => {
    if (!activeCategory) return [];
    return products.filter(p => p.product?.categoryId === activeCategory.id || p.product?.categoryId?._id === activeCategory.id);
  }, [products, activeCategory]);

  /* Build Lightbox playlist */
  const activePlaylist = useMemo(() => {
    if (isAuth) {
      if (!activeProduct) return [];
      return activeProduct.product?.catalogs || [];
    } else {
      if (!activeProduct) return [];
      return activeProduct.catalogs || [];
    }
  }, [isAuth, activeProduct]);

  const nextSlide = () => {
    setLightboxIdx(prev => (prev + 1) % activePlaylist.length);
  };

  const prevSlide = () => {
    setLightboxIdx(prev => (prev - 1 + activePlaylist.length) % activePlaylist.length);
  };

  /* Keyboard Navigation for lightbox */
  useEffect(() => {
    if (!lightboxItem) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') setLightboxItem(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxItem, activePlaylist.length]);

  if (loading) {
    return (
      <div style={S.page(isGuestMode)} className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 size={36} className="animate-spin text-[#0A5F38]" />
        <span className="text-sm mt-3 font-semibold text-slate-500">Loading catalog gallery...</span>
      </div>
    );
  }

  /* ─── RENDERING PUBLIC GUEST VIEW ───────────────────────────────────────── */
  if (isGuestMode) {
    if (!activeProduct && !categoryId) {
      return (
        <div style={S.page(true)} className="flex flex-col items-center justify-center p-8 text-center min-h-screen">
          <AlertCircle size={48} color="#f5576c" />
          <h3 className="text-xl font-bold mt-4 text-white">Product Gallery Unavailable</h3>
          <p className="text-sm text-slate-400 max-w-sm mt-2">
            The link might have expired or is invalid. Please contact our support team.
          </p>
        </div>
      );
    }

    const currentMedia = activeProduct?.catalogs || [];
    const whatsappMsg = encodeURIComponent(
      `Hi IdealTrendz! I'm browsing the product gallery and I'm interested in *${activeProduct?.name || categoryName || 'your products'}*. Could you share more details?`
    );
    const rawPhone = (company?.contactNumber || '9494307895').replace(/\D/g, '');
    const waPhone = rawPhone.length === 10 ? `91${rawPhone}` : rawPhone;
    const waLink = `https://wa.me/${waPhone}?text=${whatsappMsg}`;

    return (
      <div style={S.page(true)}>
        <header style={S.header(true)}>
          <div style={S.brandLogo}>IdealTrendz</div>
          <span style={S.badge('#f093fb')}>
            <Star size={10} fill="#f093fb" /> GALLERY
          </span>
        </header>

        <div style={S.container}>
          {viewMode === 'list' ? (
            <div>
              <div className="mb-8 flex items-center justify-between border-b border-white/5 pb-4">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {activeCategory ? `All ${activeCategory.name} Products` : 'All Products'}
                  </h2>
                  <p className="text-xs text-slate-400">Select any product to view its design photos, videos and details.</p>
                </div>
                {!isGalleryView && (
                  <button
                    onClick={() => setViewMode('detail')}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl text-xs font-bold hover:bg-white/10 transition-all cursor-pointer"
                  >
                    ← Back to Showcase
                  </button>
                )}
              </div>

              {/* Category Filter Tabs */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-b border-white/5 pb-4 mb-6">
                  {!categoryId && (
                    <button
                      style={S.categoryTab(activeCategory === null)}
                      onClick={() => {
                        setActiveCategory(null);
                      }}
                      className="transition-colors text-xs font-semibold"
                    >
                      📁 All Categories
                    </button>
                  )}
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      style={S.categoryTab(activeCategory?.id === cat.id)}
                      onClick={() => {
                        setActiveCategory(cat);
                        // Auto-bypass product list for Interiors direct dump
                        if (cat.name?.toLowerCase() === 'interiors') {
                          const catProducts = allProducts.filter(p => p.categoryId === cat.id);
                          if (catProducts.length === 1) {
                            setActiveProduct(catProducts[0]);
                            setViewMode('detail');
                            setLightboxItem(null);
                          } else {
                            setActiveProduct(null);
                            setViewMode('list');
                          }
                        } else {
                          setActiveProduct(null);
                          setViewMode('list');
                        }
                      }}
                      className="transition-colors text-xs font-semibold"
                    >
                      📁 {cat.name}
                    </button>
                  ))}
                </div>
              )}

              {guestGridProducts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {guestGridProducts.map(rp => {
                    const firstCatalog = rp.catalogs?.find(c => c.fileType === 'image') || rp.catalogs?.[0];
                    const previewUrl = firstCatalog ? getMediaUrl(firstCatalog.url) : null;
                    const price = rp.estimationCost || rp.sellingPrice;

                    return (
                      <div
                        key={rp.id}
                        style={S.glass(true)}
                        className="p-5 flex flex-col justify-between hover:scale-[1.02] hover:border-white/20 transition-all duration-300 group cursor-pointer"
                        onClick={() => {
                          setActiveProduct(rp);
                          setViewMode('detail');
                          setLightboxItem(null);
                        }}
                      >
                        <div>
                          {/* Preview Image */}
                          <div className="w-full aspect-[16/10] bg-[#0f172a] rounded-xl overflow-hidden mb-4 border border-white/5 relative">
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt={rp.name}
                                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 gap-1">
                                <ImageIcon size={28} className="opacity-45" />
                                <span className="text-[10px] font-semibold">No catalogs uploaded</span>
                              </div>
                            )}
                            {rp.catalogs?.length > 0 && (
                              <span className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded">
                                📷 {rp.catalogs.length} files
                              </span>
                            )}
                          </div>

                          {/* Title & Desc */}
                          <h3 className="text-lg font-bold text-white group-hover:text-[#f093fb] transition-colors line-clamp-1">{rp.name}</h3>
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2 min-h-[2rem] leading-relaxed">
                            {rp.description || 'No description available.'}
                          </p>
                        </div>

                        {/* Bottom Section */}
                        <div className="border-t border-white/5 pt-4 mt-4 flex items-center justify-between">
                          <div>
                            {price ? (
                              <>
                                <span className="text-[10px] text-slate-500 block uppercase font-semibold">Starting from</span>
                                <span className="text-sm font-extrabold text-white">₹{price.toLocaleString('en-IN')}</span>
                                <span className="text-[10px] text-slate-400 ml-0.5">/ {rp.estimationUnit || 'unit'}</span>
                              </>
                            ) : (
                              <span className="text-[10px] text-[#f093fb] font-semibold block">Price on Request</span>
                            )}
                          </div>

                          <span
                            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#0A5F38] text-white rounded-lg text-xs font-bold hover:bg-[#084c2e] transition-colors"
                          >
                            View Gallery <ArrowRight size={12} />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-16 bg-white/3 border border-white/5 rounded-2xl text-slate-500 text-center w-full">
                  <ImageIcon size={36} className="opacity-40" />
                  <span className="text-sm mt-3 font-semibold text-slate-400">No products uploaded under this category yet</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Related Products navigation tabs & View All button */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/5 pb-4 mb-6 gap-3">
                {guestRelatedProducts.length > 1 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {guestRelatedProducts.map(rp => (
                      <button
                        key={rp.id}
                        style={S.categoryTab(rp.id === activeProduct.id)}
                        onClick={() => {
                          setActiveProduct(rp);
                          setLightboxItem(null);
                        }}
                        className="transition-colors"
                      >
                        {rp.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                  <button
                    onClick={() => {
                      if (activeProduct?.categoryId) {
                        const currentCat = categories.find(c => c.id === activeProduct.categoryId);
                        if (currentCat) {
                          setActiveCategory(currentCat);
                        }
                      }
                      setViewMode('list');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl text-xs font-bold hover:bg-white/10 transition-all cursor-pointer"
                  >
                    📂 View All {categoryName} ({guestRelatedProducts.length})
                  </button>
                  {!categoryId && (
                    <button
                      onClick={() => {
                        setActiveCategory(null);
                        setViewMode('list');
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl text-xs font-bold hover:bg-white/10 transition-all cursor-pointer"
                    >
                      📁 View All Categories
                    </button>
                  )}
                </div>
              </div>

              {/* Double Column Showcase layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left side - Media Grid */}
                <div className="lg:col-span-8">
                  <h2 className="text-2xl font-bold text-white mb-2">{activeProduct.name}</h2>
                  <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                    {activeProduct.description || 'No description available.'}
                  </p>

                  {currentMedia.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
                      {currentMedia.map((item, index) => (
                        <MediaCardItem
                          key={item._id || index}
                          item={item}
                          isAuth={false}
                          onSelect={() => {
                            setLightboxIdx(index);
                            setLightboxItem(item);
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-12 bg-white/3 rounded-xl border border-white/5 text-slate-500">
                      <ImageIcon size={32} className="opacity-40" />
                      <span className="text-sm mt-2 font-medium">No media uploaded</span>
                    </div>
                  )}
                </div>

                {/* Right side - Specifications, details & CTAs */}
                <div className="lg:col-span-4 lg:sticky lg:top-[100px]">
                  <div style={S.glass(true)} className="p-6">
                    {activeProduct.sellingPrice || activeProduct.estimationCost ? (
                      <div className="mb-6">
                        <span className="text-xs text-slate-500 block font-semibold uppercase tracking-wider mb-1">Starting from</span>
                        <span className="text-3xl font-extrabold text-white">
                          ₹{(activeProduct.estimationCost || activeProduct.sellingPrice).toLocaleString('en-IN')}
                        </span>
                        <span className="text-xs text-slate-400 ml-1">per {activeProduct.estimationUnit || 'unit'}</span>
                      </div>
                    ) : (
                      <div className="mb-6 p-4 bg-white/3 border border-white/5 rounded-xl">
                        <span className="text-sm font-semibold text-[#f093fb] block">✨ Custom Price on Request</span>
                        <p className="text-xs text-slate-400 mt-1">
                          Our designers will provide a detailed quote matching your exact size and material choices.
                        </p>
                      </div>
                    )}

                    {/* Technical specs */}
                    {(activeProduct.materialType || activeProduct.colorFinish) && (
                      <div className="space-y-4 mb-6 border-t border-white/5 pt-4">
                        <span className="text-xs text-slate-500 block font-bold uppercase tracking-wider">Specifications</span>
                        {activeProduct.materialType && (
                          <div className="flex gap-2.5 items-start">
                            <Layers size={14} className="text-[#a5b4fc] mt-0.5" />
                            <div>
                              <span className="text-[10px] text-slate-500 block uppercase font-semibold">Material</span>
                              <span className="text-sm text-slate-200">{activeProduct.materialType}</span>
                            </div>
                          </div>
                        )}
                        {activeProduct.colorFinish && (
                          <div className="flex gap-2.5 items-start">
                            <Palette size={14} className="text-[#f093fb] mt-0.5" />
                            <div>
                              <span className="text-[10px] text-slate-500 block uppercase font-semibold">Color / Finish</span>
                              <span className="text-sm text-slate-200">{activeProduct.colorFinish}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lead Capture Form */}
                    <div className="border-t border-white/5 pt-4">
                      <span className="text-xs text-slate-500 block font-bold uppercase tracking-wider mb-3">Request Callback / Quote</span>

                      {leadSuccess ? (
                        <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
                          <span className="text-sm font-semibold text-emerald-400 block mb-1">✨ Request Submitted!</span>
                          <p className="text-xs text-slate-400">
                            Thank you. Our designers will call you back shortly.
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleLeadSubmit} className="space-y-3">
                          <div>
                            <input
                              type="text"
                              value={leadName}
                              onChange={(e) => setLeadName(e.target.value)}
                              placeholder="Your Name"
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors"
                            />
                          </div>

                          <div>
                            <input
                              type="tel"
                              value={leadPhone}
                              onChange={(e) => setLeadPhone(e.target.value)}
                              placeholder="Phone Number (10 digits)"
                              required
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors"
                            />
                          </div>

                          <div>
                            <input
                              type="email"
                              value={leadEmail}
                              onChange={(e) => setLeadEmail(e.target.value)}
                              placeholder="Email Address (Optional)"
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors"
                            />
                          </div>

                          <div>
                            <textarea
                              value={leadAddress}
                              onChange={(e) => setLeadAddress(e.target.value)}
                              placeholder="Site Address"
                              rows="2"
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors resize-none"
                            />
                          </div>

                          <div>
                            <span className="text-[10px] text-slate-500 block uppercase font-semibold mb-1 pl-1">Preferred Date *</span>
                            <input
                              type="date"
                              value={leadPreferredDate}
                              min={getMinLeadDate()}
                              onChange={(e) => setLeadPreferredDate(e.target.value)}
                              required
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors"
                            />
                          </div>

                          <div>
                            <span className="text-[10px] text-slate-500 block uppercase font-semibold mb-1 pl-1">Preferred Time *</span>
                            <input
                              type="time"
                              value={leadPreferredTime}
                              onChange={(e) => setLeadPreferredTime(e.target.value)}
                              required
                              className="w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-medium placeholder-slate-500 focus:outline-none focus:border-[#0A5F38] transition-colors"
                            />
                            {isOutOfHours(leadPreferredTime) && (
                              <div className="text-[10px] text-amber-400 font-medium px-1 mt-1 leading-relaxed">
                                ℹ️ Since this time is outside our standard visiting hours (9:30 AM - 7:30 PM), our team will contact you to check availability.
                              </div>
                            )}
                          </div>

                          {leadError && (
                            <div className="text-[11px] text-red-400 font-semibold px-1">
                              ⚠️ {leadError}
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={leadSubmitting}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A5F38] hover:bg-[#084c2e] disabled:bg-[#0A5F38]/40 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-lg shadow-emerald-950/20"
                          >
                            {leadSubmitting ? (
                              <>
                                <Loader2 size={13} className="animate-spin" />
                                Submitting Request...
                              </>
                            ) : (
                              'Request Call Back / Quote'
                            )}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="text-center py-12 text-slate-600 text-xs border-t border-white/5 mt-16">
          <div className="flex items-center justify-center gap-4 mb-3">
            <a
              href="https://idealtrendz.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A5F38]/10 text-[#0A5F38] hover:bg-[#0A5F38]/20 transition-colors text-xs font-semibold border border-[#0A5F38]/15"
            >
              <Globe size={13} /> idealtrendz.org
            </a>
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-500/10 text-slate-500 hover:bg-slate-500/20 transition-colors text-xs font-semibold border border-slate-500/15"
            >
              <ShieldCheck size={13} /> Privacy Policy
            </a>
          </div>
          <div>© {new Date().getFullYear()} IdealTrendz. All rights reserved.</div>
          <div className="flex items-center justify-center gap-1.5 mt-1 text-[10px]">
            <ShieldCheck size={12} /> Public share link protected by AES-256 equivalent view token
          </div>
        </footer>

        {/* Lightbox Viewer */}
        {lightboxItem && (
          <div style={S.lightboxOverlay} onClick={() => setLightboxItem(null)}>
            <div
              className="relative w-full p-4 flex items-center justify-center"
              style={{
                maxWidth: (activePlaylist[lightboxIdx]?.fileType === 'pdf' || activePlaylist[lightboxIdx]?.fileType === 'document') ? '92vw' : '56rem',
                maxHeight: '90vh',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Media viewer */}
              {activePlaylist[lightboxIdx]?.fileType === 'video' ? (
                <video src={getMediaUrl(activePlaylist[lightboxIdx].url)} controls className="max-w-full max-h-[80vh] rounded-lg" autoPlay />
              ) : activePlaylist[lightboxIdx]?.fileType === 'zip' ? (
                <div className="bg-slate-900 border border-white/10 rounded-2xl p-12 text-center flex flex-col items-center gap-4">
                  <FileText size={64} className="text-blue-400" />
                  <span className="text-white font-semibold text-lg max-w-md truncate">{activePlaylist[lightboxIdx].originalName}</span>
                  <a href={getMediaUrl(activePlaylist[lightboxIdx].url)} target="_blank" rel="noreferrer" style={S.ctaBtn('primary')}>
                    <Download size={16} /> Download File
                  </a>
                </div>
              ) : activePlaylist[lightboxIdx]?.fileType === 'pdf' || activePlaylist[lightboxIdx]?.fileType === 'document' ? (
                <div className="w-full flex flex-col items-center gap-3" style={{ height: '80vh' }}>
                  <div className="flex items-center justify-between w-full px-2">
                    <span className="text-white font-semibold text-sm truncate max-w-xs">
                      {activePlaylist[lightboxIdx].originalName || 'Document'}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={getMediaUrl(activePlaylist[lightboxIdx].url)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...S.ctaBtn('outline'), fontSize: '0.75rem', padding: '6px 14px' }}
                      >
                        <Download size={13} /> Download
                      </a>
                      <button
                        onClick={() => setLightboxItem(null)}
                        className="p-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors border border-white/20"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  <object
                    data={`${getMediaUrl(activePlaylist[lightboxIdx].url)}#toolbar=1&navpanes=0&view=FitH`}
                    type="application/pdf"
                    className="w-full flex-1 rounded-xl border border-white/10 bg-white"
                    style={{ minHeight: 0, height: '100%' }}
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-slate-900 rounded-xl">
                      <FileText size={48} className="text-blue-400" />
                      <p className="text-slate-300 text-sm text-center">Your browser cannot preview this PDF inline.</p>
                      <a href={getMediaUrl(activePlaylist[lightboxIdx].url)} target="_blank" rel="noreferrer" style={S.ctaBtn('primary')}>
                        <ExternalLink size={14} /> Open PDF
                      </a>
                    </div>
                  </object>
                </div>
              ) : (
                <ZoomableImage
                  src={getMediaUrl(activePlaylist[lightboxIdx].url)}
                  alt="Product view"
                />
              )}

              {/* Close Button — hidden for PDF since X is embedded in the PDF header row */}
              {activePlaylist[lightboxIdx]?.fileType !== 'pdf' && activePlaylist[lightboxIdx]?.fileType !== 'document' && (
                <button
                  className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors z-50 border border-white/10"
                  onClick={() => setLightboxItem(null)}
                >
                  <X size={20} />
                </button>
              )}

              {/* Arrow navigation */}
              {activePlaylist.length > 1 && (
                <>
                  <button
                    className="absolute left-4 p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors border border-white/10"
                    onClick={prevSlide}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="absolute right-4 p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors border border-white/10"
                    onClick={nextSlide}
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── RENDERING AUTHENTICATED MANAGEMENT CONSOLE (ADMIN/EMPLOYEE) ───────── */
  return (
    <div className="flex flex-col h-[calc(100vh-85px)] -mt-2 -mb-8 pt-2 pb-4">
      {/* Admin Header with Copy Link Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gallery Management</h1>
          <p className="text-sm text-slate-500">Upload and manage media for your product catalogs.</p>
        </div>
        <button
          onClick={handleCopyGalleryUrl}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#0A5F38] text-white rounded-xl text-sm font-bold shadow-sm hover:bg-[#084c2e] transition-colors whitespace-nowrap self-start sm:self-auto"
        >
          <Copy size={16} />
          Copy Gallery URL
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start flex-1 lg:overflow-hidden min-h-0 w-full">
        {/* Left Side: Categories sidebar List */}
        <div className="w-full lg:w-[260px] bg-white border border-slate-200 rounded-2xl p-3.5 lg:p-4 flex-shrink-0 flex flex-col h-auto lg:h-full">
          <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block mb-2 lg:mb-3 pl-1 lg:pl-2 flex-shrink-0">Product Categories</span>
          <div className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto pb-2 lg:pb-0 gap-2 lg:gap-1 flex-1 no-scrollbar w-full">
            {categories.map(cat => (
              <div key={cat.id} className="relative group flex-shrink-0 whitespace-nowrap">
                <button
                  onClick={() => {
                    setActiveCategory(cat);
                    setActiveProduct(null);
                  }}
                  className={`w-full text-left px-3.5 py-2.5 lg:py-3 pr-8 lg:pr-10 rounded-xl transition-all duration-200 font-semibold text-xs lg:text-sm whitespace-nowrap flex items-center gap-1.5 ${activeCategory?.id === cat.id
                      ? 'bg-[#E6F0EB] text-[#0A5F38]'
                      : 'text-slate-600 hover:bg-slate-50 bg-slate-50/50 lg:bg-transparent'
                    }`}
                >
                  <span>📁</span> <span>{cat.name}</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const slug = toSlug(cat.name);
                    // Append a timestamp so every shared URL is unique — this prevents
                    // WhatsApp from showing a stale cached preview image.
                    const ts = Date.now();
                    const url = `${window.location.origin}/view/category/${slug}/${cat.id}?t=${ts}`;
                    navigator.clipboard.writeText(url)
                      .then(() => toast.success(`Link for ${cat.name} copied!`))
                      .catch(() => toast.error('Failed to copy link.'));
                  }}
                  title={`Copy Guest Link for ${cat.name}`}
                  className="absolute right-1.5 lg:right-2 top-1/2 -translate-y-1/2 p-1 lg:p-1.5 text-slate-400 hover:text-[#0A5F38] hover:bg-slate-200/60 rounded-md transition-colors opacity-60 lg:opacity-40 group-hover:opacity-100"
                >
                  <Copy size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Products & Media Grids */}
        <div className="flex-1 w-full space-y-6 lg:h-full lg:overflow-y-auto pr-0 lg:pr-2 pb-10">
          {activeCategory ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-xl font-bold text-slate-800">Browse: {activeCategory.name}</h2>
                <span className="text-xs bg-[#E6F0EB] text-[#0A5F38] font-bold px-2.5 py-1 rounded-full">
                  {activeProducts.length} Items
                </span>
              </div>

              {activeProducts.length > 0 ? (
                <div className="space-y-6">
                  {(activeCategory?.name?.toLowerCase() === 'interiors' 
                    ? (activeProducts.length > 0 ? [{
                        ...activeProducts[0],
                        product: {
                          ...activeProducts[0].product,
                          catalogs: activeProducts.reduce((acc, curr) => {
                            const cats = curr.product?.catalogs || [];
                            // attach source product ID to each catalog item so deletion still works
                            return [...acc, ...cats.map(c => ({...c, _sourceProductId: curr._id || curr.id}))];
                          }, [])
                        }
                      }] : [])
                    : activeProducts
                  ).map(prod => (
                    <div key={prod._id || prod.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            {activeCategory?.name?.toLowerCase() !== 'interiors' && (
                              <h3 className="text-lg font-bold text-slate-800">{prod.product?.name}</h3>
                            )}
                            {activeCategory?.name?.toLowerCase() === 'interiors' && (
                              <h3 className="text-lg font-bold text-slate-800">Interiors</h3>
                            )}
                            {prod.product?.sku && activeCategory?.name?.toLowerCase() !== 'interiors' && (
                              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded uppercase tracking-wider">
                                SKU: {prod.product.sku}
                              </span>
                            )}
                          </div>
                          {prod.product?.description && (
                            <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
                              {prod.product.description}
                            </p>
                          )}
                        </div>

                        {/* Direct File Uploader Zone */}
                        <div className="flex items-center gap-2 relative">
                          {selectedMediaIds.filter(id => (prod.product?.catalogs || []).some(c => c._id === id)).length > 0 && (
                            <button
                              onClick={() => handleMultiDeleteFiles(prod._id || prod.id)}
                              disabled={uploadingProdId === (prod._id || prod.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-xl cursor-pointer transition-all duration-200 shadow-md shadow-red-900/10 disabled:opacity-50"
                            >
                              <Trash2 size={13} />
                              Delete Selected ({selectedMediaIds.filter(id => (prod.product?.catalogs || []).some(c => c._id === id)).length})
                            </button>
                          )}
                          <button
                            onClick={() => setUploadModalProduct(prod)}
                            disabled={uploadingProdId === (prod._id || prod.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-[#0A5F38] hover:bg-[#084c2e] text-white text-xs font-bold rounded-xl cursor-pointer transition-all duration-200 shadow-md shadow-emerald-900/10 disabled:opacity-50"
                          >
                            {uploadingProdId === (prod._id || prod.id) ? (
                              <>
                                <Loader2 size={13} className="animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Upload size={13} />
                                Add Photos / Media
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Display products media */}
                      {(prod.product?.catalogs || []).length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
                          {prod.product.catalogs.map((item, index) => (
                            <MediaCardItem
                              key={item._id || index}
                              item={item}
                              isAuth={true}
                              isSelected={selectedMediaIds.includes(item._id)}
                              onToggleSelect={handleToggleMediaSelect}
                              onDelete={(fileId) => handleDeleteFile(item._sourceProductId || prod._id || prod.id, fileId)}
                              onSelect={() => {
                                setActiveProduct(prod);
                                setLightboxIdx(index);
                                setLightboxItem(item);
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center p-8 bg-slate-50 border border-slate-200/60 rounded-xl text-slate-400 mt-4">
                          <ImageIcon size={28} className="opacity-40" />
                          <span className="text-xs font-semibold mt-1">No photos or media uploaded. Click button to add files.</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
                  <ImageIcon size={36} className="opacity-45 mx-auto" />
                  <p className="text-sm font-semibold mt-2">No products added in this category yet.</p>
                  {activeCategory?.name?.toLowerCase() === 'interiors' ? (
                    <button
                      onClick={async () => {
                        try {
                          const payload = {
                            product: { name: 'Interiors Portfolio', categoryId: activeCategory.id },
                            supplier: { shopName: 'Internal', contactNumber: '0000000000' }
                          };
                          await axios.post('/admin/products', payload);
                          toast.success('Interiors portfolio initialized!');
                          fetchProducts();
                        } catch (err) {
                          toast.error('Failed to initialize interiors portfolio');
                        }
                      }}
                      className="mt-4 px-4 py-2 bg-[#0A5F38] text-white text-xs font-bold rounded-xl hover:bg-[#084c2e] transition-colors mx-auto flex items-center justify-center"
                    >
                      Initialize Direct Upload Zone
                    </button>
                  ) : (
                    <p className="text-xs mt-1">Add products to this category in Product Management page.</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-slate-400">
              <ImageIcon size={40} className="opacity-45 mx-auto" />
              <p className="text-sm font-semibold mt-2">Please select a product category on the left sidebar.</p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Viewer (Authenticated) */}
      {lightboxItem && (
        <div style={S.lightboxOverlay} onClick={() => setLightboxItem(null)}>
          <div
            className="relative w-full p-4 flex items-center justify-center"
            style={{
              maxWidth: (activePlaylist[lightboxIdx]?.fileType === 'pdf' || activePlaylist[lightboxIdx]?.fileType === 'document') ? '92vw' : '56rem',
              maxHeight: '90vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Media viewer */}
            {activePlaylist[lightboxIdx]?.fileType === 'video' ? (
              <video src={getMediaUrl(activePlaylist[lightboxIdx].url)} controls className="max-w-full max-h-[80vh] rounded-lg" autoPlay />
            ) : activePlaylist[lightboxIdx]?.fileType === 'zip' ? (
              <div className="bg-slate-900 border border-white/10 rounded-2xl p-12 text-center flex flex-col items-center gap-4">
                <FileText size={64} className="text-blue-400" />
                <span className="text-white font-semibold text-lg max-w-md truncate">{activePlaylist[lightboxIdx].originalName}</span>
                <a href={getMediaUrl(activePlaylist[lightboxIdx].url)} target="_blank" rel="noreferrer" style={S.ctaBtn('primary')}>
                  <Download size={16} /> Download File
                </a>
              </div>
            ) : activePlaylist[lightboxIdx]?.fileType === 'pdf' || activePlaylist[lightboxIdx]?.fileType === 'document' ? (
              <div className="w-full flex flex-col items-center gap-3" style={{ height: '80vh' }}>
                <div className="flex items-center justify-between w-full px-2">
                  <span className="text-white font-semibold text-sm truncate max-w-xs">
                    {activePlaylist[lightboxIdx].originalName || 'Document'}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a
                      href={getMediaUrl(activePlaylist[lightboxIdx].url)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ ...S.ctaBtn('outline'), fontSize: '0.75rem', padding: '6px 14px' }}
                    >
                      <Download size={13} /> Download
                    </a>
                    <button
                      onClick={() => setLightboxItem(null)}
                      className="p-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors border border-white/20"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <object
                  data={`${getMediaUrl(activePlaylist[lightboxIdx].url)}#toolbar=1&navpanes=0&view=FitH`}
                  type="application/pdf"
                  className="w-full flex-1 rounded-xl border border-white/10 bg-white"
                  style={{ minHeight: 0, height: '100%' }}
                >
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-slate-900 rounded-xl">
                    <FileText size={48} className="text-blue-400" />
                    <p className="text-slate-300 text-sm text-center">Your browser cannot preview this PDF inline.</p>
                    <a href={getMediaUrl(activePlaylist[lightboxIdx].url)} target="_blank" rel="noreferrer" style={S.ctaBtn('primary')}>
                      <ExternalLink size={14} /> Open PDF
                    </a>
                  </div>
                </object>
              </div>
            ) : (
              <ZoomableImage
                src={getMediaUrl(activePlaylist[lightboxIdx].url)}
                alt="Product view"
              />
            )}

            {/* Close Button — hidden for PDF since X is embedded in the PDF header row */}
            {activePlaylist[lightboxIdx]?.fileType !== 'pdf' && activePlaylist[lightboxIdx]?.fileType !== 'document' && (
              <button
                className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors z-50 border border-white/10"
                onClick={() => setLightboxItem(null)}
              >
                <X size={20} />
              </button>
            )}

            {/* Arrow navigation */}
            {activePlaylist.length > 1 && (
              <>
                <button
                  className="absolute left-4 p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors border border-white/10"
                  onClick={prevSlide}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="absolute right-4 p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors border border-white/10"
                  onClick={nextSlide}
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalProduct && (
        <div style={S.lightboxOverlay} onClick={() => {
          setUploadModalProduct(null);
          setSelectedUploadFiles([]);
        }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="font-bold text-lg text-slate-800">Upload Media for {uploadModalProduct.product?.name}</h3>
              <button
                className="p-1.5 text-slate-400 hover:bg-slate-200 rounded-lg transition-colors"
                onClick={() => {
                  setUploadModalProduct(null);
                  setSelectedUploadFiles([]);
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="relative border-2 border-dashed border-[#0A5F38]/30 rounded-2xl p-8 text-center bg-[#0A5F38]/5 hover:bg-[#0A5F38]/10 transition-colors">
                <input
                  type="file"
                  multiple
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setSelectedUploadFiles([...selectedUploadFiles, ...Array.from(e.target.files)]);
                    }
                    e.target.value = null;
                  }}
                />
                <Upload size={32} className="mx-auto text-[#0A5F38]/60 mb-3" />
                <p className="text-sm font-bold text-slate-700">Click or drag files here to select</p>
                <p className="text-xs text-slate-500 mt-1">Supports images, videos, PDFs, and ZIP files</p>
              </div>

              <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-2">
                <AlertCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 font-medium">
                  <strong>Note:</strong> Maximum file size is 2GB per file. Large files may take a few minutes to upload depending on your internet connection. Please do not close the window until the upload is confirmed.
                </p>
              </div>

              {selectedUploadFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-bold text-slate-700 mb-3">Selected Files ({selectedUploadFiles.length})</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedUploadFiles.map((file, idx) => (
                      <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-100 flex items-center justify-center">
                        {file.type.startsWith('image/') ? (
                          <img src={URL.createObjectURL(file)} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center gap-2 p-2">
                            <FileText size={24} className="text-slate-400" />
                            <span className="text-[10px] text-slate-500 text-center truncate w-full px-1">{file.name}</span>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            const newFiles = [...selectedUploadFiles];
                            newFiles.splice(idx, 1);
                            setSelectedUploadFiles(newFiles);
                          }}
                          className="absolute top-1.5 right-1.5 p-1 bg-white shadow-md text-red-500 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => {
                  setUploadModalProduct(null);
                  setSelectedUploadFiles([]);
                }}
                className="px-4 py-2 font-semibold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                disabled={selectedUploadFiles.length === 0 || uploadingProdId}
                onClick={() => {
                  handleUploadFiles(uploadModalProduct._id || uploadModalProduct.id, selectedUploadFiles);
                }}
                className="px-5 py-2 font-bold text-white bg-[#0A5F38] hover:bg-[#084c2e] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors text-sm flex items-center gap-2"
              >
                {uploadingProdId ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : 'Confirm Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
